<template>
    <div v-for="instance in queue" class="notification flash" :style="'--tw-translate-y: ' + ((! instance.active ? 0 : getIndent(instance)) * 64) + 'px'" :class="[ instance.type, instance.toggleType, instance.customClass, { active: instance.active, 'is-hiding': instance.isHiding }]" @click.prevent="hideIfAllowed(instance)">
        <div class="notification-body">
            {{ instance.message }}
        </div>
    </div>
</template>

<script lang="ts">

import {getBoolFromAttribute, nextFrame, delay} from "@/frontend-v3/Utils";

export interface FlashData {
    type?: string,
    message?: string,
    class?: string,
    persist?: boolean
}

const defaultData = {
    type: 'error',
    message: 'error',
    toggleType: 'remove'
}

class FlashMessage {
    type: string;
    message: string;
    customClass: string;
    toggleType: string;
    hideDelay: number;
    isHiding: boolean;
    active: boolean;

    constructor(props: FlashData) {
        this.type = props.type || defaultData.type
        this.message = props.message || defaultData.message;
        this.customClass = props.class || '';
        this.toggleType = props.persist === true ? 'persist' : defaultData.toggleType;

        this.hideDelay = 3500;
        this.isHiding = true;
        this.active = true;
    }
}

export default {

    name: "FlashMessage",

    props: ['hasErrors', 'flashData', 'defaultMessage'],

    data() {
        return {
            queue: [] as FlashMessage[],
        }
    },

    created() {
        // let shouldShow = getBoolFromAttribute(this.hasErrors)
        defaultData.message = this.defaultMessage;

        if (this.flashData) {
            const instance = new FlashMessage(this.flashData);
            this.queue.push(instance);

            // if (shouldShow) {
                this.show(instance);
            // }
        }
    },

    mounted() {
        this.Bus.on('hide-flash-message', () => {
            this.hideAll();
        });

        this.Bus.on('show-flash-message', (data : FlashData) => {
            const instance = new FlashMessage(data);
            this.queue.push(instance);
            this.show(instance);
        });
    },

    methods: {
        /**
         * Hides all flash messages.
         */
        hideAll() {
            this.queue.forEach((instance: FlashMessage) => this.hide(instance));
        },

        /**
         * Starts the animation to hide the notification.
         * @param instance
         */
        async hide(instance: FlashMessage) {
            instance.isHiding = true;
            this.$forceUpdate();

            await delay(500);

            instance.active = false;
            this.$forceUpdate();

            this.clearIfNoneVisible();
        },

        /**
         * Checks if the instance can be hidden by clicking on it.
         * @param instance
         */
        hideIfAllowed(instance: FlashMessage) {
            if (instance.toggleType === 'persist') {
                this.hide(instance);
            }
        },

        /**
         * Starts the animation for showing a new flash message, and sets up the hiding process if it's not persistent.
         * @param instance
         */
        async show(instance: FlashMessage) {
            await nextFrame();
            instance.isHiding = false;
            this.$forceUpdate();

            if (instance.toggleType !== 'persist') {
                await delay(instance.hideDelay);

                this.hide(instance);
            }
        },

        /**
         * Gets the indentation the notification should use (how many items 'down' the list).
         * @param {FlashMessage} instance
         */
        getIndent(instance: FlashMessage) {
            let index = 0;
            const l = this.queue.filter((i: FlashMessage) => i.active && ! i.isHiding);
            l.some((i: FlashMessage) => {
                ++index;
                return i === instance;
            });

            return l.length - index;
        },

        /**
         * Resets the queue if no items are visible. We cannot clear the queue while items are shown
         * because Vue will re-use existing items, screwing with animations and stuff.
         */
        clearIfNoneVisible() {
            if (this.queue.some((i: FlashMessage) => i.active)) {
                return;
            }

            // Reset the queue
            this.queue = [];
        },
    }
}
</script>
